import type { EntradaPublica } from '../api/ops/webPublicaCliente';
import { ErrorApi } from '../api/problema';

/**
 * Los estados de «cerrado» de la entrada pública de un negocio (`getPublicEntry`, G885, G932), que comparten la reserva
 * pública (8.11) y la compra de entradas (10.13):
 *
 * - `pausado`: `status: closed` con `read_only` (el negocio en solo lectura).
 * - `temporada`: `seasonal_pause`, con el día que vuelve a abrir (`reopens_on`).
 * - `retirada`: `platform_takedown`; la página no dice por qué.
 * - `baja`: `410` (negocio dado de baja o sin ningún bloque que venda). No llega la marca: la página va con los colores de
 *   Reservaria y sin el nombre del negocio (D-052).
 */
export type MotivoCierre = 'pausado' | 'temporada' | 'retirada';

export interface Cierre {
    motivo: MotivoCierre;
    /** `reopens_on` (fecha local, AAAA-MM-DD) con `temporada`; si no, nulo. */
    vuelve: string | null;
}

/** El cierre de una entrada pública; `null` si está abierta. */
export function cierreDeEntrada(e: Pick<EntradaPublica, 'status' | 'closed_reason' | 'reopens_on'>): Cierre | null {
    if (e.status !== 'closed') {
        return null;
    }
    switch (e.closed_reason) {
        case 'seasonal_pause':
            return { motivo: 'temporada', vuelve: e.reopens_on ?? null };
        case 'platform_takedown':
            return { motivo: 'retirada', vuelve: null };
        default:
            return { motivo: 'pausado', vuelve: null };
    }
}

/** Marca del `410` de `getPublicEntry`: solo esa respuesta es «baja» (no un `410` de otra operación). */
export const ENTRADA_DE_BAJA = 'entrada-de-baja';

/** Lee la entrada pública y convierte su `410` en `ENTRADA_DE_BAJA`. */
export async function leerEntrada(leer: () => Promise<EntradaPublica>): Promise<EntradaPublica> {
    try {
        return await leer();
    } catch (x) {
        if (x instanceof ErrorApi && x.estado === 410) {
            throw new Error(ENTRADA_DE_BAJA, { cause: x });
        }
        throw x;
    }
}

export const esBaja = (x: unknown): boolean => x instanceof Error && x.message === ENTRADA_DE_BAJA;
